import { ButtonHTMLAttributes } from 'react';

import useMediaDuration from '@/hooks/shared/useMediaDuration';

import { formatFileSize } from '@/utils/formatFileSize';
import { openDownloadLink } from '@/utils/openDownloadLink';

import Spinner from '@/components/shared/ui/loading/Spinner';

import theme from '@/styles/theme';

import FileInfo from '@/types/fileInfo';

import { IconArrowDownToBracketRound, IconXmarkRound } from '../icon';
import {
  ColorType,
  SizeType,
  VariantType,
  chipContainerClass,
  chipIconWrapperClass,
  chipSubTextClass,
  chipTextClass,
  chipTextWrapperClass,
} from './chipStyles';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  file: FileInfo;
  color?: ColorType;
  variant?: VariantType;
  size?: SizeType;
  showSubText?: boolean;
  showDeleteIcon?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
}

/**
 * file 관련 기능이 필요하지 않은 경우, 일반 `<Chip/>` 컴포넌트 사용
 * - 파일 형식이 오디오나 비디오일 경우, 재생 시간을 SubText에 표시합니다 (그 외의 chipSubText prop은 무시)
 * - onClick 이벤트 발생 시, file_storage_path를 통해 다운로드를 시도합니다 (별도의 onClick 이벤트 바인딩 가능)
 */
export default function FileUploadChip({
  file,
  color = 'primary',
  variant = 'fill',
  size = 'md',
  showSubText = true,
  showDeleteIcon = false,
  onClick,
  onRemove,
  className,
  ...props
}: Props) {
  const mediaDuration = useMediaDuration(file);

  const handleRemove = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation(); // 이벤트 버블링 방지
    onRemove?.();
  };

  return (
    <>
      {mediaDuration === undefined ? (
        <Spinner variant="admin" className="scale-50 h-[28px]" />
      ) : (
        // emotion styled 가 하던 것처럼 외부에서 넘어온 className 을 이어 붙인다
        <div
          className={`${chipContainerClass({ color, variant, size })} ${className ?? ''}`}
        >
          <button
            type="button"
            className="contents"
            onClick={() => {
              openDownloadLink(file);
              onClick?.();
            }}
            {...props}
          >
            <div className={chipIconWrapperClass({ position: 'left', size })}>
              <IconArrowDownToBracketRound
                size={size === 'sm' ? 12 : 16}
                color={theme`colors.apps.brandGrayWhite`}
              />
            </div>

            <div className={chipTextWrapperClass(size)}>
              <p className={chipTextClass({ size, variant, color })}>
                {file.file_client_name}
              </p>
              {showSubText && (
                <small className={chipSubTextClass({ variant, color })}>
                  {mediaDuration && (
                    <span>
                      {mediaDuration}
                      <span className="mx-[2px]">·</span>
                    </span>
                  )}
                  {formatFileSize(Number(file.file_size))}
                </small>
              )}
            </div>
          </button>

          {showDeleteIcon && (
            <button
              type="button"
              aria-label="첨부 파일 삭제"
              onClick={handleRemove}
              className={chipIconWrapperClass({ position: 'right' })}
            >
              <IconXmarkRound size={12} />
            </button>
          )}
        </div>
      )}
    </>
  );
}
